export const DEPLOYMENT_DURATION_OPTIONS = [
  "Less than 3 months",
  "3-12 months",
  "1-3 years",
  "3+ years",
  "Permanent",
] as const;

export const PRIMARY_OBJECTIVE_OPTIONS = [
  "Progress monitoring",
  "Safety & compliance",
  "Environmental monitoring",
  "Security & surveillance",
  "Asset inspection",
  "Time-lapse documentation",
  "Other",
] as const;

export const EXPANSION_OPTIONS = [
  "None",
  "1-5 additional cameras",
  "5-10 additional cameras",
  "10-25 additional cameras",
  "25+ additional cameras",
] as const;

export const SITE_CONDITION_OPTIONS = [
  "Remote / off-grid",
  "Urban / on-grid",
  "Hazardous environment",
  "Indoor",
  "Underground",
  "Marine / coastal",
  "High altitude",
] as const;

export const MOUNTING_TYPE_OPTIONS = [
  "Pole",
  "Wall / facade",
  "Scaffold",
  "Vehicle / mobile",
  "Tripod / temporary",
  "Other",
] as const;

export const CAPTURE_FREQUENCY_OPTIONS = [
  "Every 15 minutes",
  "Every 30 minutes",
  "Hourly",
  "2-3 times per day",
  "Once per day",
  "Custom schedule",
] as const;

export const OPERATING_HOURS_OPTIONS = [
  "24/7",
  "Daylight hours only",
  "Business hours (9-5)",
  "Custom schedule",
] as const;

export const HISTORICAL_STORAGE_OPTIONS = [
  "30 days",
  "90 days",
  "6 months",
  "12 months",
  "Unlimited",
] as const;

export const AI_DETECTION_OPTIONS = [
  "People / PPE detection",
  "Vehicle detection",
  "Object detection",
  "Change detection",
  "Weather / visibility",
  "Construction progress",
  "Safety violations",
  "Environmental changes",
  "Custom model",
] as const;

export const ALERT_BEHAVIOUR_OPTIONS = [
  "Only when condition is triggered",
  "Scheduled summary reports",
  "Real-time continuous",
  "Threshold-based",
] as const;

export const AI_QUERIES_PER_IMAGE_OPTIONS = [
  "1",
  "2-3",
  "5+",
  "Custom",
] as const;

export const ALERT_CHANNEL_OPTIONS = [
  "Email",
  "SMS",
  "Push notification",
  "Webhook",
  "Microsoft Teams",
  "Slack",
] as const;

export const PLATFORM_FEATURE_OPTIONS = [
  "Live camera view",
  "Historical image browser",
  "AI query interface",
  "Dashboard & analytics",
  "Report generation",
  "User management",
  "API access",
  "White-label branding",
] as const;

export const INTEGRATION_OPTIONS = [
  "Power BI",
  "Procore",
  "Autodesk BIM 360",
  "SharePoint",
  "Microsoft Teams",
  "Slack",
  "Custom API integration",
  "None",
] as const;

export const COMPLIANCE_OPTIONS = [
  "GDPR",
  "ISO 27001",
  "SOC 2",
  "Industry-specific",
  "None",
] as const;

export const DATA_RETENTION_OPTIONS = [
  "30 days",
  "90 days",
  "6 months",
  "12 months",
  "Custom",
] as const;

export const COMMERCIAL_MODEL_OPTIONS = [
  "Rental / subscription",
  "Purchase + subscription",
  "Pay per image",
  "Enterprise agreement",
] as const;

export const FREQUENCY_TO_IMAGES_PER_DAY: Record<string, number> = {
  "Every 15 minutes": 96,
  "Every 30 minutes": 48,
  "Hourly": 24,
  "2-3 times per day": 3,
  "Once per day": 1,
  "Custom schedule": 0,
};

export const AI_QUERY_MAP: Record<string, number> = {
  "1": 1,
  "2-3": 3,
  "5+": 5,
  "Custom": 0,
};

export type MonthlyExpansionYear = Record<string, number>;

export interface MonthlyExpansion {
  "2026": MonthlyExpansionYear;
  "2027": MonthlyExpansionYear;
  "2028": MonthlyExpansionYear;
}

export const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"] as const;

export const DEFAULT_MONTHLY_YEAR: MonthlyExpansionYear = Object.fromEntries(
  MONTHS.map((m) => [m, 0])
);

export const DEFAULT_MONTHLY_EXPANSION: MonthlyExpansion = {
  "2026": { ...DEFAULT_MONTHLY_YEAR },
  "2027": { ...DEFAULT_MONTHLY_YEAR },
  "2028": { ...DEFAULT_MONTHLY_YEAR },
};

export interface QuestionnaireFormData {
  projectName: string;
  locations: number;
  deploymentDuration: string;
  primaryObjective: string;
  camerasInitial: number;
  expansion: string;
  cameras2026: number;
  cameras2027: number;
  cameras2028: number;
  monthlyExpansion: MonthlyExpansion;
  siteConditions: string[];
  mountingType: string;
  captureFrequency: string;
  operatingHours: string;
  customImagesPerDay: number;
  onDemandImages: string;
  historicalStorage: string;
  aiRequired: string;
  aiDetections: string[];
  alertBehaviour: string;
  aiQueriesPerImage: string;
  aiQueriesPerImageCustom: number;
  alertRecipients: string[];
  alertChannels: string[];
  platformAccess: string;
  usersRequiringAccess: number;
  platformFeatures: string[];
  integrations: string[];
  customIntegration: string;
  compliance: string[];
  dataRetention: string;
  commercialModel: string;
  budgetRange: string;
  successCriteria: string;
  notes: string;
}

export const DEFAULT_FORM_DATA: QuestionnaireFormData = {
  projectName: "",
  locations: 1,
  deploymentDuration: "3-12 months",
  primaryObjective: "Progress monitoring",
  camerasInitial: 1,
  expansion: "None",
  cameras2026: 0,
  cameras2027: 0,
  cameras2028: 0,
  monthlyExpansion: { ...DEFAULT_MONTHLY_EXPANSION },
  siteConditions: [],
  mountingType: "Pole",
  captureFrequency: "Hourly",
  operatingHours: "24/7",
  customImagesPerDay: 24,
  onDemandImages: "No",
  historicalStorage: "90 days",
  aiRequired: "Yes",
  aiDetections: [],
  alertBehaviour: "Only when condition is triggered",
  aiQueriesPerImage: "1",
  aiQueriesPerImageCustom: 1,
  alertRecipients: [],
  alertChannels: [],
  platformAccess: "Yes",
  usersRequiringAccess: 1,
  platformFeatures: [],
  integrations: [],
  customIntegration: "",
  compliance: [],
  dataRetention: "90 days",
  commercialModel: "Rental / subscription",
  budgetRange: "",
  successCriteria: "",
  notes: "",
};

export interface CalculatedResults {
  monthlyImages: number;
  monthlyAiQueries: number;
  packageRecommendation: string;
  imagesPerDay: number;
  aiQueriesPerImage: number;
  monthlyImagesPerCamera: number;
  totalCameras: number;
}
